// Slice 12 — CGC governance dashboard (DESIGN §5.5 / §9.3).
//
// External clinical / legal advisers (the besideU CGC) review and approve
// `system_prompts` rows here. Two surfaces: (a) "Prompts" lists every row
// in modes the calling member can act on, with inline approve/reject; (b)
// "My decisions" lists this member's audit-trail history.
//
// The dashboard intentionally keeps approve/reject inline (no dedicated
// detail route) so a reviewer can read the prompt body + decide + record
// dissent in one place. The full decision audit for each prompt is loaded
// on demand when the user expands a row.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { StubSourceBadge } from '@/components/ui/StubSourceBadge.tsx';
import { useSession } from '@/lib/auth.ts';
import type {
  ClassifierCategoryInspectorEntry,
  ClassifierCategoryInspectorResponse,
} from '@besideu/shared/admin';
import type { PromptApprovalMode } from '@besideu/shared/maeve';
import type {
  QaRubricDimensionEntry,
  QaRubricInspectorResponse,
  QaSampleDetail,
  QaSampleSummary,
  QaScoreValue,
} from '@besideu/shared/qa-rubric';
import type {
  DictionaryInspectorEntry,
  DictionaryInspectorPhrase,
  DictionaryInspectorResponse,
  DictionaryTier,
} from '@besideu/shared/dictionary';
import type {
  AdversarialScenarioInspectorEntry,
  AdversarialScenarioInspectorResponse,
  AdversarialScenarioRef,
} from '@besideu/shared/adversarial-scenarios';

// PromptMode is the union of every prompt mode the loader knows about
// (HR_Maeve, Manager_Guidance, Employee_Companion, Policy_Audit + the
// three Policy_Audit_* sub-modes, Trajectory_Classifier, AI_Disclosure,
// Companion_* templates, Boilerplate_*, DPA, QA_Judge). Sourced from
// `PROMPT_MODE_APPROVAL_REQUIREMENTS` so it stays in lockstep with the
// approval map — no local restatement to drift from.
type PromptMode = PromptApprovalMode;

type CgcMemberKind = 'grief_counsellor' | 'mhp' | 'gp_psychiatrist' | 'legal_counsel' | 'product';

interface CgcMe {
  id: string;
  name: string;
  email: string;
  kind: CgcMemberKind;
  modesAllowed: readonly string[];
}

interface PromptListEntry {
  id: string;
  mode: PromptMode;
  version: string;
  promptTextPreview: string;
  isActive: boolean;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedByKind: CgcMemberKind | null;
  approvedAt: string | null;
  approvedJointlyBy: string | null;
  approvedJointlyByName: string | null;
  approvedJointlyByKind: CgcMemberKind | null;
  approvedJointlyAt: string | null;
  requiresJointSignOff: boolean;
  status:
    | 'awaiting_first_approval'
    | 'awaiting_joint_approval'
    | 'approved'
    | 'active'
    | 'inactive';
  activatedAt: string | null;
  deactivatedAt: string | null;
  changeSummary: string | null;
  createdAt: string;
}

interface PromptDetail {
  prompt: PromptListEntry & { promptText: string };
  decisions: Array<{
    id: string;
    cgcMemberId: string;
    cgcMemberName: string;
    cgcMemberKind: CgcMemberKind;
    decision: 'approved' | 'rejected';
    reason: string | null;
    createdAt: string;
  }>;
}

interface DecisionListEntry {
  id: string;
  cgcMemberId: string;
  cgcMemberName: string;
  cgcMemberKind: CgcMemberKind;
  decision: 'approved' | 'rejected';
  reason: string | null;
  createdAt: string;
}

// Slice 23 — unreviewed-incidents wire type. Mirrors the API response
// from `GET /api/cgc/incidents`.
interface IncidentListEntry {
  id: string;
  organizationId: string;
  organizationName: string;
  triggeringCategory: string | null;
  eventType: 'Red_Flag_Fired';
  createdAt: string;
}

// CGC dashboard section IDs. The `<CgcSectionIndex />` quick-jump nav
// at the top of the dashboard and the `id="..."` attribute on each
// section both reference this constant — a single source of truth so
// a future section addition only needs to update one place (the
// `SECTIONS` array below) plus the new section's wrapper element.
const CGC_SECTION_IDS = {
  classifierCategories: 'cgc-section-classifier-categories',
  dictionary: 'cgc-section-dictionary',
  adversarialScenarios: 'cgc-section-adversarial-scenarios',
  qaRubric: 'cgc-section-qa-rubric',
  qaSamples: 'cgc-section-qa-samples',
  incidents: 'cgc-section-incidents',
  prompts: 'cgc-section-prompts',
  decisions: 'cgc-section-decisions',
} as const;

const SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: CGC_SECTION_IDS.classifierCategories, label: 'Classifier categories' },
  { id: CGC_SECTION_IDS.dictionary, label: 'Traffic Light Dictionary' },
  { id: CGC_SECTION_IDS.adversarialScenarios, label: 'Adversarial scenarios' },
  { id: CGC_SECTION_IDS.qaRubric, label: 'QA scoring rubric' },
  { id: CGC_SECTION_IDS.qaSamples, label: 'QA samples to score' },
  { id: CGC_SECTION_IDS.incidents, label: 'Unreviewed incidents' },
  { id: CGC_SECTION_IDS.prompts, label: 'Prompts in your remit' },
  { id: CGC_SECTION_IDS.decisions, label: 'My decisions' },
];

export function CgcDashboard() {
  const session = useSession('cgc-shell');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ['cgc', 'me'],
    queryFn: () => api.get<CgcMe>('/api/cgc/me'),
  });

  const prompts = useQuery({
    queryKey: ['cgc', 'system-prompts'],
    queryFn: () => api.get<{ prompts: PromptListEntry[] }>('/api/cgc/system-prompts'),
  });

  // Slice 23 — Unreviewed incidents (DESIGN §5.5 / §9.4 #9). The 24-hour
  // Mon–Fri SLA is anchored on this list; we poll every 30s while the
  // dashboard is open so a freshly-fired red flag surfaces without a
  // manual refresh.
  const incidents = useQuery({
    queryKey: ['cgc', 'incidents'],
    queryFn: () => api.get<{ incidents: IncidentListEntry[] }>('/api/cgc/incidents'),
    refetchInterval: 30_000,
  });

  const decisions = useQuery({
    queryKey: ['cgc', 'decisions'],
    queryFn: () => api.get<{ decisions: DecisionListEntry[] }>('/api/cgc/decisions'),
  });

  const logout = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSuccess: async () => {
      queryClient.clear();
      await navigate('/cgc/sign-in');
    },
  });

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clinical Governance Council</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {me.data ? (
              <>
                {me.data.name} · {kindLabel(me.data.kind)} ·{' '}
                <span className="font-mono text-xs">{session.userId.slice(0, 8)}…</span>
              </>
            ) : (
              <>Operator {session.userId.slice(0, 8)}…</>
            )}
          </p>
        </div>
        <Button variant="ghost" onClick={() => logout.mutate()} loading={logout.isPending}>
          Sign out
        </Button>
      </header>

      <CgcSectionIndex />

      <ClassifierCategoryReviewSection memberKind={me.data?.kind ?? null} />

      <DictionaryReviewSection memberKind={me.data?.kind ?? null} memberId={me.data?.id ?? null} />

      <AdversarialScenarioReviewSection
        memberKind={me.data?.kind ?? null}
        memberId={me.data?.id ?? null}
      />

      <QaRubricReviewSection memberKind={me.data?.kind ?? null} />

      <QaSampleScoringSection memberKind={me.data?.kind ?? null} memberId={me.data?.id ?? null} />

      <section className="mt-10" id={CGC_SECTION_IDS.incidents}>
        <h2 className="text-lg font-semibold">Unreviewed incidents</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Red-flag events awaiting your clinical review. The 24-hour Mon–Fri SLA is anchored on the
          time each incident was logged. Marking an incident as reviewed records your decision in
          the legal-defensibility audit trail and removes it from this list.
        </p>
        {incidents.isPending ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : incidents.error ? (
          <p className="mt-4 text-sm text-traffic-red" role="status">
            Could not load incidents.
          </p>
        ) : incidents.data.incidents.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No unreviewed incidents.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {incidents.data.incidents.map((i) => (
              <IncidentCard key={i.id} incident={i} />
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10" id={CGC_SECTION_IDS.prompts}>
        <h2 className="text-lg font-semibold">Prompts in your remit</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Maeve system prompts you may approve or reject. Approval pointers + your decision are
          appended to the legal-defensibility audit trail. Out-of-scope modes are filtered
          server-side and do not appear here.
        </p>
        {prompts.isPending ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : prompts.error ? (
          <p className="mt-4 text-sm text-traffic-red" role="status">
            Could not load prompts.
          </p>
        ) : prompts.data.prompts.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="mt-4 space-y-3">
            {prompts.data.prompts.map((p) => (
              <PromptCard
                key={p.id}
                prompt={p}
                memberId={me.data?.id ?? null}
                memberKind={me.data?.kind ?? null}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="mt-12" id={CGC_SECTION_IDS.decisions}>
        <h2 className="text-lg font-semibold">My decisions</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your historical approvals and rejections, newest first. This is the audit trail surfaced
          to legal counsel in compliance review.
        </p>
        {decisions.isPending ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : decisions.error ? (
          <p className="mt-4 text-sm text-traffic-red" role="status">
            Could not load decisions.
          </p>
        ) : decisions.data.decisions.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No decisions recorded yet.
          </p>
        ) : (
          <DecisionsTable decisions={decisions.data.decisions} />
        )}
      </section>
    </main>
  );
}

function EmptyState() {
  return (
    <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
      <p>No prompts to review at the moment.</p>
      <p className="mt-2">
        Drafts authored by besideU staff that fall within your remit will appear here when
        they&rsquo;re ready for review.
      </p>
    </div>
  );
}

function PromptCard({
  prompt,
  memberId,
  memberKind,
}: {
  prompt: PromptListEntry;
  memberId: string | null;
  memberKind: CgcMemberKind | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  // Slice 13 post-review — success-message state lives locally so it can be
  // dismissed on card collapse or future card actions. Driving it from
  // `activate.data` (the previous approach) meant the green "Activated"
  // banner persisted for the lifetime of the card mount even after the
  // refreshed query showed the prompt was no longer current — confusing
  // when reviewed in a later session.
  const [activateSuccess, setActivateSuccess] = useState<{ deactivated: string[] } | null>(null);
  const queryClient = useQueryClient();

  // On first expand, fetch the full detail (text + decision audit). Cached
  // per-prompt — flipping closed and open again uses the cache.
  const detail = useQuery({
    queryKey: ['cgc', 'system-prompts', prompt.id],
    queryFn: () => api.get<PromptDetail>(`/api/cgc/system-prompts/${prompt.id}`),
    enabled: expanded,
  });

  const approve = useMutation({
    mutationFn: () => api.post<PromptDetail>(`/api/cgc/system-prompts/${prompt.id}/approve`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'system-prompts'] });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'decisions'] });
    },
  });

  const reject = useMutation({
    mutationFn: (reason: string) =>
      api.post<{ ok: true }>(`/api/cgc/system-prompts/${prompt.id}/reject`, { reason }),
    onSuccess: () => {
      setShowRejectForm(false);
      setRejectReason('');
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'system-prompts'] });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'system-prompts', prompt.id] });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'decisions'] });
    },
  });

  // Slice 13 — activation. Only enabled when the prompt has reached the
  // `approved` status (single-mode fully approved, OR joint-mode both slots
  // filled). The backend re-checks this via the loader's
  // `isFullyApproved()`; the button-level disable is the UX hint.
  const activate = useMutation({
    mutationFn: () =>
      api.post<{ prompt: PromptListEntry; deactivated: string[] }>(
        `/api/cgc/system-prompts/${prompt.id}/activate`,
        {},
      ),
    onSuccess: (data) => {
      // Stash the displaced-siblings count into local state so the success
      // banner is dismiss-on-collapse rather than persisting for the
      // lifetime of the card mount.
      setActivateSuccess({ deactivated: data.deactivated });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'system-prompts'] });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'system-prompts', prompt.id] });
    },
  });

  // Disable Approve in three cases:
  //   1. Prompt already fully approved (status terminal).
  //   2. Joint-required + the calling member's kind already holds the first
  //      slot (same kind can't dual-sign).
  //   3. The calling member is already one of the approvers — they can't
  //      sign off twice. Without this branch (Slice 12 post-review fix),
  //      a member who took the first slot would see an enabled Approve
  //      button that always fails server-side with `already_approved_by_you`.
  //
  // `selfAlreadyApproved` is permanent for a given row: rejection writes a
  // decision row but deliberately does NOT clear the approval pointers
  // (see `cgc-governance.ts` `reject()` — rejection is recorded dissent,
  // not an approval undo). So once a member approves a row, the button on
  // that specific row stays disabled for them forever, even if other
  // members later reject. That's the right conservative behaviour: the
  // server-side state machine has no path that would let the same member
  // re-claim a slot they already filled, and re-revisions land as fresh
  // `system_prompts` rows with their own ids. If the reject semantics
  // ever change to clear the pointers, this UI condition will need a
  // matching adjustment.
  const sameKindAlreadyApproved =
    prompt.requiresJointSignOff &&
    prompt.approvedByKind !== null &&
    memberKind !== null &&
    prompt.approvedByKind === memberKind &&
    prompt.approvedJointlyBy === null;
  const selfAlreadyApproved =
    memberId !== null && (prompt.approvedBy === memberId || prompt.approvedJointlyBy === memberId);
  const fullyApproved =
    prompt.status === 'approved' || prompt.status === 'active' || prompt.status === 'inactive';
  const approveDisabled = fullyApproved || sameKindAlreadyApproved || selfAlreadyApproved;

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">
            {prompt.mode} <span className="text-xs text-muted-foreground">v{prompt.version}</span>
          </p>
          <StatusBadge status={prompt.status} requiresJoint={prompt.requiresJointSignOff} />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            // Collapsing clears the activation success banner — it's a
            // post-action confirmation, not a sticky status.
            if (expanded) setActivateSuccess(null);
            setExpanded(!expanded);
          }}
          className="py-1 text-sm"
        >
          {expanded ? 'Collapse' : 'Review'}
        </Button>
      </div>

      <p className="mt-3 line-clamp-3 text-sm whitespace-pre-wrap text-muted-foreground">
        {prompt.promptTextPreview}
      </p>

      {prompt.approvedByName ? (
        <p className="mt-3 text-xs text-muted-foreground">
          First approval: {prompt.approvedByName} ({kindLabel(prompt.approvedByKind)})
          {prompt.requiresJointSignOff && prompt.approvedJointlyByName ? (
            <>
              {' '}
              · joint sign-off: {prompt.approvedJointlyByName} (
              {kindLabel(prompt.approvedJointlyByKind)})
            </>
          ) : prompt.requiresJointSignOff ? (
            <> · awaiting joint sign-off from a different adviser kind</>
          ) : null}
        </p>
      ) : null}

      {expanded ? (
        <div className="mt-4 space-y-4 border-t border-border pt-4">
          {detail.isPending ? (
            <p className="text-sm text-muted-foreground">Loading full text…</p>
          ) : detail.error ? (
            <p className="text-sm text-traffic-red" role="status">
              Could not load prompt detail.
            </p>
          ) : (
            <>
              <div>
                <p className="text-xs tracking-wide text-muted-foreground uppercase">Full text</p>
                <pre className="mt-1 max-h-96 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
                  {detail.data.prompt.promptText}
                </pre>
              </div>
              {detail.data.decisions.length > 0 ? (
                <div>
                  <p className="text-xs tracking-wide text-muted-foreground uppercase">
                    Decision history
                  </p>
                  <ul className="mt-1 space-y-1 text-xs">
                    {detail.data.decisions.map((d) => (
                      <li key={d.id} className="rounded border border-border bg-background p-2">
                        <span
                          className={
                            d.decision === 'approved' ? 'text-foreground' : 'text-traffic-red'
                          }
                        >
                          {d.decision === 'approved' ? 'Approved' : 'Rejected'}
                        </span>{' '}
                        by {d.cgcMemberName} ({kindLabel(d.cgcMemberKind)}) at{' '}
                        {new Date(d.createdAt).toLocaleString()}
                        {d.reason ? <p className="mt-1 text-muted-foreground">{d.reason}</p> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => approve.mutate()}
              loading={approve.isPending}
              disabled={approveDisabled}
              className="py-1.5 text-sm"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowRejectForm(!showRejectForm)}
              className="py-1.5 text-sm"
            >
              {showRejectForm ? 'Cancel reject' : 'Reject'}
            </Button>
            {/* Slice 13 — activation lives on the same card. Enabled only when
                the prompt is in the `approved` status (single fully approved
                OR joint both slots filled). The button is rendered always so
                the affordance is visible at the right state-machine moment
                and disabled (with explanatory copy) the rest of the time. */}
            <Button
              variant="secondary"
              onClick={() => activate.mutate()}
              loading={activate.isPending}
              disabled={prompt.status !== 'approved' || activate.isPending}
              className="py-1.5 text-sm"
            >
              Activate
            </Button>
          </div>

          {approve.error ? <ApproveError error={approve.error} /> : null}
          {activate.error ? <ActivateError error={activate.error} /> : null}
          {activateSuccess ? (
            <p role="status" className="text-sm text-traffic-green-text">
              Activated.{' '}
              {activateSuccess.deactivated.length > 0
                ? `Replaced ${activateSuccess.deactivated.length} previously-active prompt(s) in the same scope.`
                : 'No previous active prompt was in this scope.'}
            </p>
          ) : null}

          {showRejectForm ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (rejectReason.trim().length === 0) return;
                reject.mutate(rejectReason.trim());
              }}
              className="space-y-2"
            >
              <label className="block">
                <span className="text-xs tracking-wide text-muted-foreground uppercase">
                  Reason (required)
                </span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="What needs to change?"
                  className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-3 text-base"
                  rows={3}
                  maxLength={2000}
                  required
                />
              </label>
              <Button
                type="submit"
                variant="secondary"
                loading={reject.isPending}
                disabled={rejectReason.trim().length === 0 || reject.isPending}
                className="py-1.5 text-sm"
              >
                Submit rejection
              </Button>
              {reject.error ? (
                <p className="text-sm text-traffic-red" role="status">
                  Could not record the rejection. Try again.
                </p>
              ) : null}
            </form>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function StatusBadge({
  status,
  requiresJoint,
}: {
  status: PromptListEntry['status'];
  requiresJoint: boolean;
}) {
  const { label, tone } = statusLabel(status, requiresJoint);
  return (
    <span
      className={[
        'mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal uppercase',
        tone === 'pending' ? 'bg-muted text-muted-foreground' : '',
        tone === 'partial' ? 'bg-traffic-amber/10 text-traffic-amber-text' : '',
        tone === 'ready' ? 'bg-traffic-green/10 text-traffic-green-text' : '',
        tone === 'live' ? 'bg-primary/10 text-primary' : '',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function statusLabel(
  status: PromptListEntry['status'],
  requiresJoint: boolean,
): { label: string; tone: 'pending' | 'partial' | 'ready' | 'live' } {
  switch (status) {
    case 'awaiting_first_approval':
      return {
        label: requiresJoint ? 'Awaiting first sign-off' : 'Awaiting approval',
        tone: 'pending',
      };
    case 'awaiting_joint_approval':
      return { label: 'Awaiting joint sign-off', tone: 'partial' };
    case 'approved':
      return { label: 'Approved — ready to activate', tone: 'ready' };
    case 'active':
      return { label: 'Active in production', tone: 'live' };
    case 'inactive':
      return { label: 'Inactive (historical)', tone: 'pending' };
  }
}

function ApproveError({ error }: { error: unknown }) {
  let msg = 'Could not record the approval. Try again.';
  if (error instanceof ApiError) {
    if (error.message === 'already_approved_by_you') msg = 'You have already approved this prompt.';
    else if (error.message === 'same_kind_already_approved') {
      msg =
        'Another adviser of your kind has already approved. Joint sign-off requires a different kind.';
    } else if (error.message === 'already_fully_approved') {
      msg = 'This prompt has already been fully approved.';
    }
  }
  return (
    <p className="text-sm text-traffic-red" role="status">
      {msg}
    </p>
  );
}

// Slice 13 — activation error messages. Maps the route's 409 reasons to
// operator-friendly copy. 404 surfaces as the generic fallback (the button
// shouldn't fire for an out-of-mode prompt anyway — the dashboard wouldn't
// have listed it).
function ActivateError({ error }: { error: unknown }) {
  let msg = 'Could not activate this prompt. Try again.';
  if (error instanceof ApiError) {
    if (error.message === 'not_approved') {
      msg =
        'This prompt is not fully approved yet. Joint-sign-off modes need both required adviser kinds before activation.';
    } else if (error.message === 'missing_boilerplate') {
      msg =
        'This prompt is missing required §4.7 boilerplate (Intended Use, Human Oversight, Prohibited Use Cases). The drafter needs to revise it before it can be activated.';
    } else if (error.message === 'already_active') {
      msg = 'This prompt is already active in production.';
    } else if (error.message === 'inactive_cannot_reactivate') {
      // Don't imply an "explicit rollback" affordance exists — it doesn't.
      // Direct the user at the BesideU admin escalation path, which is the
      // honest answer until/unless a rollback surface is built.
      msg =
        'This prompt was previously active and has been displaced by a newer version. Reactivating a displaced prompt isn’t supported from this dashboard — if a rollback is genuinely needed, contact a besideU administrator who can coordinate the change.';
    }
  }
  return (
    <p className="text-sm text-traffic-red" role="status">
      {msg}
    </p>
  );
}

function DecisionsTable({ decisions }: { decisions: DecisionListEntry[] }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-2 font-medium">When</th>
            <th className="px-4 py-2 font-medium">Decision</th>
            <th className="px-4 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((d) => (
            <tr key={d.id} className="border-t border-border">
              <td className="px-4 py-2 align-top text-muted-foreground">
                {new Date(d.createdAt).toLocaleString()}
              </td>
              <td className="px-4 py-2 align-top">
                <span
                  className={d.decision === 'approved' ? 'text-foreground' : 'text-traffic-red'}
                >
                  {d.decision === 'approved' ? 'Approved' : 'Rejected'}
                </span>
              </td>
              <td className="px-4 py-2 align-top text-muted-foreground">{d.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Slice 23 — Unreviewed incident card. Shows the triggering category, the
// organisation it came from, age relative to now (with an overdue badge
// when past 24 working hours), and a Mark-as-reviewed action with an
// optional notes textarea. The 24-hour Mon–Fri SLA is anchored on
// `createdAt`; weekend hours don't count against the budget — a flag
// fired Friday at 16:00 is "due" at Tuesday 16:00 if Mon is a public
// holiday too, but we render a simple calendar-24h indicator without
// holiday-awareness for MVP. Operators can opt to a sweep-time alert if
// the holiday-aware variant is needed later.
function IncidentCard({ incident }: { incident: IncidentListEntry }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);
  const review = useMutation({
    mutationFn: (body: { notes: string | null }) =>
      api.post<{ ok: true; alreadyReviewed: boolean }>(
        `/api/cgc/incidents/${incident.id}/review`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cgc', 'incidents'] }),
  });

  const ageMs = Date.now() - new Date(incident.createdAt).getTime();
  const overdue = ageMs > 24 * 60 * 60 * 1000;
  const tone = overdue ? 'text-traffic-red' : 'text-muted-foreground';

  return (
    <li className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="inline-flex items-center rounded-full bg-traffic-red/20 px-2 py-0.5 text-xs text-traffic-red">
            {incident.triggeringCategory ?? 'uncategorised'}
          </span>
          <span className="text-sm font-medium">{incident.organizationName}</span>
        </div>
        <span className={`text-xs ${tone}`}>
          Logged{' '}
          <time dateTime={incident.createdAt}>
            {new Date(incident.createdAt).toLocaleString('en-GB')}
          </time>
          {overdue ? ' · overdue (>24h)' : ''}
        </span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Incident <span className="font-mono">{incident.id.slice(0, 8)}…</span> · organisation{' '}
        <span className="font-mono">{incident.organizationId.slice(0, 8)}…</span>. No conversational
        content is available; clinical triage proceeds via your organisation's safeguarding
        protocol.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        {expanded ? (
          <label className="flex-1 text-sm">
            <span className="mb-1 block font-medium">
              Review notes <span className="font-normal text-muted-foreground">(optional)</span>
            </span>
            <textarea
              className="block w-full rounded-md border border-border bg-background px-3 py-2 text-base"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2_000}
              placeholder="Brief note for the audit trail (no PII; no conversational content)."
            />
          </label>
        ) : (
          <Button variant="ghost" onClick={() => setExpanded(true)} disabled={review.isPending}>
            Add notes
          </Button>
        )}
        <Button
          onClick={() => review.mutate({ notes: notes.trim() === '' ? null : notes.trim() })}
          loading={review.isPending}
          disabled={review.isPending}
        >
          Mark as reviewed
        </Button>
      </div>
      {review.error ? (
        <p className="mt-2 text-sm text-traffic-red" role="alert">
          Could not record the review — please try again.
        </p>
      ) : null}
    </li>
  );
}

function kindLabel(kind: CgcMemberKind | null): string {
  switch (kind) {
    case 'grief_counsellor':
      return 'BACP grief counsellor';
    case 'mhp':
      return 'Mental health professional';
    case 'gp_psychiatrist':
      return 'GP / psychiatrist';
    case 'legal_counsel':
      return 'Legal counsel';
    case 'product':
      return 'Product';
    case null:
      return 'unknown';
  }
}

// =============================================================================
// CGC dashboard section index.
//
// Quick-jump nav for the CGC dashboard. The dashboard renders seven
// sections in the order they're most often visited (governance surfaces
// first, audit trails last); scrolling between them gets tedious once
// the CGC is in a sign-off session. The index mirrors the section
// headings + the same `id` attribute each section uses for the
// browser's native anchor scroll. Anchor links preserve the URL hash
// so a CGC can deep-link to a specific section (e.g. share
// `#cgc-section-dictionary` in a Slack thread when assigning
// dictionary work).
// =============================================================================

function CgcSectionIndex() {
  return (
    <nav
      aria-label="CGC dashboard sections"
      className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/30 px-4 py-2 text-sm"
    >
      <span className="text-xs tracking-wide text-muted-foreground uppercase">Jump to</span>
      {SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {s.label}
        </a>
      ))}
    </nav>
  );
}

// =============================================================================
// Slice 32 — classifier-category review section.
//
// Reads the same inspector payload the admin governance tab uses, but
// filters to categories the calling member's `kind` can act on (per the
// per-category `requiredKinds`). Members can submit a review with
// optional commentary; a kind that's already approved the current
// version sees the Approve button disabled with the "Already reviewed"
// reason.
// =============================================================================

function ClassifierCategoryReviewSection({ memberKind }: { memberKind: CgcMemberKind | null }) {
  const inspector = useQuery({
    queryKey: ['cgc', 'classifier-categories'],
    queryFn: () => api.get<ClassifierCategoryInspectorResponse>('/api/cgc/classifier-categories'),
  });

  const actionable = inspector.data?.categories.filter((c) =>
    memberKind ? c.requiredKinds.includes(memberKind) : false,
  );

  return (
    <section className="mt-10" id={CGC_SECTION_IDS.classifierCategories}>
      <h2 className="text-lg font-semibold">Classifier categories in your remit</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The eight CGC-candidate classifier categories per DESIGN §5.3.1. Review each category's
        description + guardrail copy and approve when you're satisfied the wording reflects clinical
        reality. Approvals are recorded against the current set version (
        <span className="font-mono">v{inspector.data?.setVersion ?? '…'}</span>); a code-side bump
        of that version invalidates prior approvals and re-surfaces categories for re-review.
      </p>
      {inspector.isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : inspector.error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          Could not load classifier categories.
        </p>
      ) : !actionable || actionable.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No categories in your remit.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {actionable.map((entry) => (
            <CgcCategoryCard key={entry.categoryKey} entry={entry} memberKind={memberKind} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CgcCategoryCard({
  entry,
  memberKind,
}: {
  entry: ClassifierCategoryInspectorEntry;
  memberKind: CgcMemberKind | null;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const alreadyApprovedByThisKind =
    entry.status.kind !== 'pending' &&
    memberKind !== null &&
    entry.status.reviewers.some((r) => r.cgcMemberKind === memberKind);

  const submit = useMutation({
    mutationFn: (body: { notes?: string }) =>
      api.post<{ ok: true }>(`/api/cgc/classifier-categories/${entry.categoryKey}/review`, body),
    onSuccess: () => {
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'classifier-categories'] });
    },
  });

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.categoryKey}</span> · routes to{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint sign-off)' : ' (single sign-off)'}
          </p>
        </div>
        <StubSourceBadge source={entry.responseGuidanceSource} />
      </div>

      <div className="mt-3 space-y-2 text-sm">
        <p className="whitespace-pre-wrap">{entry.description}</p>
        <div className="rounded-md border border-border bg-muted/20 p-3">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Guardrail copy</p>
          <p className="mt-1 text-sm whitespace-pre-wrap">{entry.guardrailCopy}</p>
        </div>
      </div>

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
          <p className="tracking-wide uppercase">Reviewers (current version)</p>
          <ul className="mt-1 space-y-0.5">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) ·{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {alreadyApprovedByThisKind ? (
        <p className="mt-3 text-xs text-muted-foreground">
          A reviewer of your kind has already approved this category for the current version.
        </p>
      ) : (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = notes.trim();
            submit.mutate(trimmed ? { notes: trimmed } : {});
          }}
        >
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Notes (optional)
            </span>
            <textarea
              className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Optional commentary recorded with the approval."
            />
          </label>
          <div>
            <Button
              type="submit"
              variant="primary"
              className="py-1.5 text-sm"
              loading={submit.isPending}
            >
              Approve for current version
            </Button>
          </div>
          {submit.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
                ? "Your CGC kind isn't in this category's required-kind set."
                : submit.error instanceof ApiError && submit.error.message === 'already_reviewed'
                  ? 'Already approved by your kind for the current version.'
                  : 'Could not submit. Try again.'}
            </p>
          ) : null}
        </form>
      )}
    </li>
  );
}

// =============================================================================
// Slice 34 — QA scoring rubric review + per-sample scoring sections.
//
// Mirrors the classifier review section pattern. Two surfaces: (a) per-
// dimension sign-off on the rubric for the current QA_RUBRIC_VERSION; (b)
// per-sample 1-3 scoring across the dimensions in the member's remit.
// =============================================================================

function QaRubricReviewSection({ memberKind }: { memberKind: CgcMemberKind | null }) {
  const inspector = useQuery({
    queryKey: ['cgc', 'qa-rubric'],
    queryFn: () => api.get<QaRubricInspectorResponse>('/api/cgc/qa-rubric'),
  });

  const actionable = inspector.data?.dimensions.filter((d) =>
    memberKind ? d.requiredKinds.includes(memberKind) : false,
  );

  return (
    <section className="mt-10" id={CGC_SECTION_IDS.qaRubric}>
      <h2 className="text-lg font-semibold">QA scoring rubric — dimensions in your remit</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The ten candidate dimensions per DESIGN §9.4 #5. Approve each dimension when you're
        satisfied the description reflects what to look for in conversation samples. Approvals are
        recorded against rubric version{' '}
        <span className="font-mono">v{inspector.data?.version ?? '…'}</span>; a code-side bump
        invalidates prior approvals.
      </p>
      {inspector.isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : inspector.error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          Could not load QA rubric.
        </p>
      ) : !actionable || actionable.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No dimensions in your remit.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {actionable.map((entry) => (
            <CgcQaDimensionCard key={entry.dimensionKey} entry={entry} memberKind={memberKind} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CgcQaDimensionCard({
  entry,
  memberKind,
}: {
  entry: QaRubricDimensionEntry;
  memberKind: CgcMemberKind | null;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const alreadyApprovedByThisKind =
    entry.status.kind !== 'pending' &&
    memberKind !== null &&
    entry.status.reviewers.some((r) => r.cgcMemberKind === memberKind);

  const submit = useMutation({
    mutationFn: (body: { notes?: string }) =>
      api.post<{ ok: true }>(`/api/cgc/qa-rubric/dimensions/${entry.dimensionKey}/review`, body),
    onSuccess: () => {
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'qa-rubric'] });
    },
  });

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.dimensionKey}</span> · {entry.designRef} · routes to{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint sign-off)' : ' (single sign-off)'}
          </p>
        </div>
      </div>

      <p className="mt-3 text-sm whitespace-pre-wrap">{entry.description}</p>

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
          <p className="tracking-wide uppercase">Reviewers (current version)</p>
          <ul className="mt-1 space-y-0.5">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) ·{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {alreadyApprovedByThisKind ? (
        <p className="mt-3 text-xs text-muted-foreground">
          A reviewer of your kind has already approved this dimension for the current version.
        </p>
      ) : (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = notes.trim();
            submit.mutate(trimmed ? { notes: trimmed } : {});
          }}
        >
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Notes (optional)
            </span>
            <textarea
              className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Optional commentary recorded with the approval."
            />
          </label>
          <div>
            <Button
              type="submit"
              variant="primary"
              className="py-1.5 text-sm"
              loading={submit.isPending}
            >
              Approve for current version
            </Button>
          </div>
          {submit.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
                ? "Your CGC kind isn't in this dimension's required-kind set."
                : submit.error instanceof ApiError && submit.error.message === 'already_reviewed'
                  ? 'Already approved by your kind for the current version.'
                  : 'Could not submit. Try again.'}
            </p>
          ) : null}
        </form>
      )}
    </li>
  );
}

function QaSampleScoringSection({
  memberKind,
  memberId,
}: {
  memberKind: CgcMemberKind | null;
  memberId: string | null;
}) {
  const samples = useQuery({
    queryKey: ['cgc', 'qa-samples'],
    queryFn: () => api.get<{ samples: QaSampleSummary[] }>('/api/cgc/qa-rubric/samples'),
  });
  const inspector = useQuery({
    queryKey: ['cgc', 'qa-rubric'],
    queryFn: () => api.get<QaRubricInspectorResponse>('/api/cgc/qa-rubric'),
  });

  // Dimensions in the calling member's remit — used to filter the
  // scoring grid so a CGC member only scores dimensions their kind is
  // responsible for.
  const dimensionsInRemit =
    inspector.data?.dimensions.filter((d) =>
      memberKind ? d.requiredKinds.includes(memberKind) : false,
    ) ?? [];

  return (
    <section className="mt-10" id={CGC_SECTION_IDS.qaSamples}>
      <h2 className="text-lg font-semibold">QA samples to score</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Admin-pasted redacted excerpts. Score each dimension in your remit on a 1–3 scale (1 = fails
        the dimension, 3 = passes). You can re-score to override a prior score.
      </p>
      {samples.isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : samples.error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          Could not load samples.
        </p>
      ) : !samples.data || samples.data.samples.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No samples awaiting scoring.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {samples.data.samples.map((s) => (
            <CgcSampleCard
              key={s.id}
              sample={s}
              dimensionsInRemit={dimensionsInRemit}
              memberId={memberId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function CgcSampleCard({
  sample,
  dimensionsInRemit,
  memberId,
}: {
  sample: QaSampleSummary;
  dimensionsInRemit: readonly QaRubricDimensionEntry[];
  memberId: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-medium">{sample.sourceLabel}</p>
          <p className="text-xs text-muted-foreground">
            {sample.mode.replaceAll('_', ' ')} · created{' '}
            {new Date(sample.createdAt).toLocaleString('en-GB')}
            {sample.createdByAdminName ? ` by ${sample.createdByAdminName}` : ''}
          </p>
        </div>
        <Button variant="ghost" className="py-1 text-sm" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Collapse' : 'Open + score'}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {sample.dimensionsScoredCount} / 10 dimensions scored · {sample.scoreRowCount} total score
        rows
      </p>
      {expanded ? (
        <CgcSampleScoringForm
          sampleId={sample.id}
          dimensionsInRemit={dimensionsInRemit}
          memberId={memberId}
        />
      ) : null}
    </li>
  );
}

function CgcSampleScoringForm({
  sampleId,
  dimensionsInRemit,
  memberId,
}: {
  sampleId: string;
  dimensionsInRemit: readonly QaRubricDimensionEntry[];
  memberId: string | null;
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['cgc', 'qa-sample', sampleId],
    queryFn: () => api.get<{ sample: QaSampleDetail }>(`/api/cgc/qa-rubric/samples/${sampleId}`),
  });

  const [scores, setScores] = useState<Record<string, QaScoreValue | undefined>>({});

  // Re-seed from server state whenever the prior-score rows change for
  // THIS member (covers the re-fetch-after-submit path). Filtered by
  // `cgcMemberId` so colleagues' edits don't trigger a re-seed that
  // would clobber unsaved local edits.
  const seedKey = detail.data
    ? detail.data.sample.scores
        .filter((s) => s.cgcMemberId === memberId)
        .map((s) => `${s.dimensionKey}:${s.score}`)
        .join('|')
    : '';
  useEffect(() => {
    if (!detail.data) return;
    const next: Record<string, QaScoreValue | undefined> = {};
    for (const sc of detail.data.sample.scores) {
      if (sc.cgcMemberId === memberId) {
        next[sc.dimensionKey] = sc.score;
      }
    }
    setScores(next);
  }, [seedKey, memberId]);

  const submit = useMutation({
    mutationFn: (body: {
      scores: { dimensionKey: string; score: QaScoreValue; notes?: string }[];
    }) => api.post<{ ok: true }>(`/api/cgc/qa-rubric/samples/${sampleId}/scores`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'qa-samples'] });
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'qa-sample', sampleId] });
    },
  });

  if (detail.isPending) {
    return <p className="mt-3 text-sm text-muted-foreground">Loading sample…</p>;
  }
  if (detail.error || !detail.data) {
    return (
      <p className="mt-3 text-sm text-traffic-red" role="alert">
        Could not load sample.
      </p>
    );
  }

  const sample = detail.data.sample;

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-4">
      <div>
        <p className="text-xs tracking-wide text-muted-foreground uppercase">Redacted transcript</p>
        <pre className="mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
          {sample.redactedTranscript}
        </pre>
      </div>

      {dimensionsInRemit.length === 0 ? (
        <p className="text-xs text-muted-foreground">No dimensions in your remit to score.</p>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const payload: { dimensionKey: string; score: QaScoreValue }[] = [];
            for (const d of dimensionsInRemit) {
              const s = scores[d.dimensionKey];
              if (s !== undefined) {
                payload.push({ dimensionKey: d.dimensionKey, score: s });
              }
            }
            if (payload.length === 0) return;
            submit.mutate({ scores: payload });
          }}
        >
          <ul className="space-y-2">
            {dimensionsInRemit.map((d) => (
              <li
                key={d.dimensionKey}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{d.displayName}</p>
                  <p className="text-xs text-muted-foreground">{d.description}</p>
                </div>
                <div
                  className="flex items-center gap-1"
                  role="radiogroup"
                  aria-label={d.displayName}
                >
                  {([1, 2, 3] as QaScoreValue[]).map((n) => {
                    const active = scores[d.dimensionKey] === n;
                    return (
                      <button
                        type="button"
                        key={n}
                        role="radio"
                        aria-checked={active}
                        className={`w-9 rounded border px-2 py-1 text-xs font-medium ${
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-muted-foreground hover:bg-muted'
                        }`}
                        onClick={() => setScores((prev) => ({ ...prev, [d.dimensionKey]: n }))}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
          {Object.keys(scores).length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Pre-filled from your prior scores. Re-submitting overrides them.
            </p>
          ) : null}
          <div>
            <Button
              type="submit"
              variant="primary"
              className="py-1.5 text-sm"
              loading={submit.isPending}
            >
              Submit scores
            </Button>
          </div>
          {submit.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              Could not submit. Try again.
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}

// =============================================================================
// Slice 47 — per-category §10.2 Traffic Light Dictionary authoring + review
// section (DESIGN §9.3 #13, §10.2).
//
// Mirrors `ClassifierCategoryReviewSection` (line 782-821) +
// `CgcCategoryCard` (line 823-931) but scoped to phrases per category
// per tier. The CGC authors per-(category, tier) phrases (the engineering
// baseline + CGC additions), edits / retires any existing phrase, and
// signs off the dictionary for the current `DICTIONARY_SET_VERSION`.
//
// Per-phrase authorship + retire are kind-gated: only CGC members whose
// `kind` is in the category's `requiredKinds` can author or retire.
// This mirrors the classifier review route's posture (line 295-336) —
// a `mhp` cannot author `safety_concerns` phrases; a `legal_counsel`
// cannot author `suicidal_ideation` phrases.
// =============================================================================

function DictionaryReviewSection({
  memberKind,
  memberId,
}: {
  memberKind: CgcMemberKind | null;
  memberId: string | null;
}) {
  const inspector = useQuery({
    queryKey: ['cgc', 'dictionary'],
    queryFn: () => api.get<DictionaryInspectorResponse>('/api/cgc/dictionary'),
  });

  const actionable = inspector.data?.categories.filter((c) =>
    memberKind ? c.requiredKinds.includes(memberKind) : false,
  );

  return (
    <section className="mt-10" id={CGC_SECTION_IDS.dictionary}>
      <h2 className="text-lg font-semibold">Traffic Light Dictionary</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The{' '}
        <a
          className="underline underline-offset-2 hover:text-foreground"
          href="https://github.com/besideU/besideU-Platform/blob/main/DESIGN.md#102-traffic-light-dictionary-for-qa--classifier-agent"
          target="_blank"
          rel="noreferrer"
        >
          §10.2
        </a>{' '}
        per-category exemplar phrases. Author green / amber / red phrases for each category; the
        non-prod model composer reads them into the system instruction alongside the engineering
        baseline. <strong>Production is unaffected</strong> — production reads the CGC-signed-off{' '}
        <code>system_prompts</code> rows, not the dictionary (the dictionary is a non-prod-only
        runtime concern + a CGC review concern). Sign off the dictionary for the current set version
        (<span className="font-mono">v{inspector.data?.setVersion ?? '…'}</span>); a code-side
        version bump invalidates prior sign-offs and re-surfaces every category for re-review.
      </p>
      {inspector.isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : inspector.error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          Could not load dictionary.
        </p>
      ) : !actionable || actionable.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No categories in your remit.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {actionable.map((entry) => (
            <DictionaryCategoryCard
              key={entry.categoryKey}
              entry={entry}
              memberKind={memberKind}
              memberId={memberId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function DictionaryCategoryCard({
  entry,
  memberKind,
  memberId,
}: {
  entry: DictionaryInspectorEntry;
  memberKind: CgcMemberKind | null;
  memberId: string | null;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const alreadyApprovedByThisKind =
    entry.status.kind !== 'pending' &&
    memberKind !== null &&
    entry.status.reviewers.some((r) => r.cgcMemberKind === memberKind);

  const submitReview = useMutation({
    mutationFn: (body: { notes?: string }) =>
      api.post<{ ok: true }>(`/api/cgc/dictionary/categories/${entry.categoryKey}/review`, body),
    onSuccess: () => {
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'dictionary'] });
    },
  });

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.categoryKey}</span> · routes to{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint sign-off)' : ' (single sign-off)'}
          </p>
        </div>
        <DictionaryStatusBadge status={entry.status} />
      </div>

      {(['red', 'amber', 'green'] as DictionaryTier[]).map((tier) => (
        <DictionaryTierSection
          key={tier}
          categoryKey={entry.categoryKey}
          tier={tier}
          phrases={entry.phrasesByTier[tier]}
        />
      ))}

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
          <p className="tracking-wide uppercase">Reviewers (current version)</p>
          <ul className="mt-1 space-y-0.5">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) ·{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {alreadyApprovedByThisKind ? (
        <p className="mt-3 text-xs text-muted-foreground">
          A reviewer of your kind has already approved this category for the current version.
        </p>
      ) : (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = notes.trim();
            submitReview.mutate(trimmed ? { notes: trimmed } : {});
          }}
        >
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Notes (optional)
            </span>
            <textarea
              className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Optional commentary recorded with the approval."
            />
          </label>
          <div>
            <Button
              type="submit"
              variant="primary"
              className="py-1.5 text-sm"
              loading={submitReview.isPending}
            >
              Approve for current version
            </Button>
          </div>
          {submitReview.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {submitReview.error instanceof ApiError &&
              submitReview.error.message === 'kind_not_allowed'
                ? "Your CGC kind isn't in this category's required-kind set."
                : submitReview.error instanceof ApiError &&
                    submitReview.error.message === 'already_reviewed'
                  ? 'Already approved by your kind for the current version.'
                  : 'Could not submit. Try again.'}
            </p>
          ) : null}
        </form>
      )}

      <p className="mt-2 text-[10px] text-muted-foreground">
        {memberId ? `Authored actions attributed to ${memberId.slice(0, 8)}…` : null}
      </p>
    </li>
  );
}

function DictionaryStatusBadge({ status }: { status: DictionaryInspectorEntry['status'] }) {
  let label = 'Pending review';
  let tone: 'pending' | 'partial' | 'ready' = 'pending';
  if (status.kind === 'partially_reviewed') {
    label = 'Partially reviewed';
    tone = 'partial';
  } else if (status.kind === 'reviewed') {
    label = 'Reviewed';
    tone = 'ready';
  }
  return (
    <span
      className={[
        'mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal uppercase',
        tone === 'pending' ? 'bg-muted text-muted-foreground' : '',
        tone === 'partial' ? 'bg-traffic-amber/10 text-traffic-amber-text' : '',
        tone === 'ready' ? 'bg-traffic-green/10 text-traffic-green-text' : '',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function DictionaryTierSection({
  categoryKey,
  tier,
  phrases,
}: {
  categoryKey: string;
  tier: DictionaryTier;
  phrases: readonly DictionaryInspectorPhrase[];
}) {
  const [adding, setAdding] = useState(false);
  const tierColor =
    tier === 'red'
      ? 'border-traffic-red/30 bg-traffic-red/5'
      : tier === 'amber'
        ? 'border-traffic-amber/30 bg-traffic-amber/5'
        : 'border-traffic-green/30 bg-traffic-green/5';

  return (
    <div className={`mt-3 rounded-md border p-3 ${tierColor}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold tracking-wide uppercase">{tier} tier</p>
        <p className="text-xs text-muted-foreground">{phrases.length} phrase(s)</p>
      </div>
      {phrases.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          No {tier}-tier phrases for this category yet.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {phrases.map((p) => (
            <DictionaryPhraseRow key={p.id} categoryKey={categoryKey} phrase={p} />
          ))}
        </ul>
      )}
      {adding ? (
        <DictionaryAddPhraseForm
          categoryKey={categoryKey}
          tier={tier}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button variant="ghost" onClick={() => setAdding(true)} className="mt-2 py-1 text-xs">
          + Add {tier} phrase
        </Button>
      )}
    </div>
  );
}

function DictionaryPhraseRow({
  categoryKey,
  phrase,
}: {
  categoryKey: string;
  phrase: DictionaryInspectorPhrase;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  const retire = useMutation({
    mutationFn: () => api.del<{ ok: true }>(`/api/cgc/dictionary/phrases/${phrase.id}`),
    onSuccess: () => {
      setConfirmingRetire(false);
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'dictionary'] });
    },
  });

  return (
    <li className="rounded border border-border bg-background p-2 text-sm">
      <p className="whitespace-pre-wrap">{phrase.phrase}</p>
      <p className="mt-1 text-[10px] text-muted-foreground">
        Authored by {phrase.authoredBy.cgcMemberName} (
        {phrase.authoredBy.cgcMemberKind.replaceAll('_', ' ')}) on{' '}
        {new Date(phrase.createdAt).toLocaleString('en-GB')}
        {phrase.editedFromPhraseId ? ' (edited from prior version)' : ''}
        {phrase.notes ? ` · ${phrase.notes}` : ''}
      </p>
      <div className="mt-2 flex gap-2">
        <Button variant="ghost" onClick={() => setEditing(!editing)} className="py-0.5 text-[11px]">
          {editing ? 'Cancel edit' : 'Edit'}
        </Button>
        {confirmingRetire ? (
          <>
            <Button
              variant="secondary"
              onClick={() => retire.mutate()}
              loading={retire.isPending}
              className="py-0.5 text-[11px]"
            >
              Confirm retire
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmingRetire(false)}
              className="py-0.5 text-[11px]"
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            onClick={() => setConfirmingRetire(true)}
            className="py-0.5 text-[11px] text-traffic-red"
          >
            Retire
          </Button>
        )}
      </div>
      {editing ? (
        <DictionaryEditPhraseForm
          phraseId={phrase.id}
          initialPhrase={phrase.phrase}
          onCancel={() => setEditing(false)}
        />
      ) : null}
      {retire.error ? (
        <p className="mt-1 text-xs text-traffic-red" role="alert">
          {retire.error instanceof ApiError && retire.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this category's required-kind set."
            : 'Could not retire the phrase. Try again.'}
        </p>
      ) : null}
      <p className="mt-1 text-[10px] text-muted-foreground">
        category: <span className="font-mono">{categoryKey}</span>
      </p>
    </li>
  );
}

function DictionaryAddPhraseForm({
  categoryKey,
  tier,
  onCancel,
}: {
  categoryKey: string;
  tier: DictionaryTier;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [phrase, setPhrase] = useState('');
  const [notes, setNotes] = useState('');

  const submit = useMutation({
    mutationFn: (body: { tier: DictionaryTier; phrase: string; notes?: string }) =>
      api.post<{ id: string }>(`/api/cgc/dictionary/categories/${categoryKey}/phrases`, body),
    onSuccess: () => {
      setPhrase('');
      setNotes('');
      onCancel();
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'dictionary'] });
    },
  });

  return (
    <form
      className="mt-2 grid gap-2 border-t border-border pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (phrase.trim().length === 0) return;
        submit.mutate({
          tier,
          phrase: phrase.trim(),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      }}
    >
      <label className="block text-sm">
        <span className="text-[10px] tracking-wide text-muted-foreground uppercase">
          New {tier} phrase
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          rows={2}
          maxLength={500}
          required
          placeholder="The exemplar phrase (verbatim, 1-500 chars)."
        />
      </label>
      <label className="block text-sm">
        <span className="text-[10px] tracking-wide text-muted-foreground uppercase">
          Notes (optional)
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={1}
          maxLength={2000}
          placeholder="Optional commentary recorded with the phrase."
        />
      </label>
      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          className="py-1 text-xs"
          loading={submit.isPending}
          disabled={phrase.trim().length === 0 || submit.isPending}
        >
          Add
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="py-1 text-xs">
          Cancel
        </Button>
      </div>
      {submit.error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this category's required-kind set."
            : 'Could not add the phrase. Try again.'}
        </p>
      ) : null}
    </form>
  );
}

function DictionaryEditPhraseForm({
  phraseId,
  initialPhrase,
  onCancel,
}: {
  phraseId: string;
  initialPhrase: string;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [phrase, setPhrase] = useState(initialPhrase);
  const [notes, setNotes] = useState('');

  const submit = useMutation({
    mutationFn: (body: { phrase: string; notes?: string }) =>
      api.patch<{ id: string }>(`/api/cgc/dictionary/phrases/${phraseId}`, body),
    onSuccess: () => {
      setNotes('');
      onCancel();
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'dictionary'] });
    },
  });

  return (
    <form
      className="mt-2 grid gap-2 border-t border-border pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (phrase.trim().length === 0) return;
        submit.mutate({
          phrase: phrase.trim(),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      }}
    >
      <label className="block text-sm">
        <span className="text-[10px] tracking-wide text-muted-foreground uppercase">
          Edit phrase
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          rows={2}
          maxLength={500}
          required
        />
      </label>
      <label className="block text-sm">
        <span className="text-[10px] tracking-wide text-muted-foreground uppercase">
          Notes (optional)
        </span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={1}
          maxLength={2000}
        />
      </label>
      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          className="py-1 text-xs"
          loading={submit.isPending}
          disabled={phrase.trim().length === 0 || submit.isPending}
        >
          Save edit
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="py-1 text-xs">
          Cancel
        </Button>
      </div>
      {submit.error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this category's required-kind set."
            : 'Could not save the edit. Try again.'}
        </p>
      ) : null}
    </form>
  );
}

// =============================================================================
// Adversarial test scenario programme (PROJECT §5 hard gate #5 / DESIGN §9.4
// #9). The CGC authors 100+ adversarial scenarios per Maeve mode and signs
// each mode's set off, version by version. Same authoring + sign-off shape as
// the Traffic Light Dictionary above, keyed on Maeve MODE (no tiers; richer
// per-scenario payload).
// =============================================================================

function AdversarialScenarioReviewSection({
  memberKind,
  memberId,
}: {
  memberKind: CgcMemberKind | null;
  memberId: string | null;
}) {
  const inspector = useQuery({
    queryKey: ['cgc', 'adversarial-scenarios'],
    queryFn: () => api.get<AdversarialScenarioInspectorResponse>('/api/cgc/adversarial-scenarios'),
  });

  const actionable = inspector.data?.modes.filter((m) =>
    memberKind ? m.requiredKinds.includes(memberKind) : false,
  );

  return (
    <section className="mt-10" id={CGC_SECTION_IDS.adversarialScenarios}>
      <h2 className="text-lg font-semibold">Adversarial scenarios</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        DESIGN §9.4 #9 / §10.2 — the 100+ adversarial test scenarios per Maeve mode that the CGC
        authors and signs off before go-live (a PROJECT §5 hard gate). Author scenarios for each
        mode in your remit, then sign the mode's set off for the current version (
        <span className="font-mono">v{inspector.data?.setVersion ?? '…'}</span>); a version bump
        invalidates prior sign-offs and re-surfaces every mode for re-review.
      </p>
      {inspector.isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : inspector.error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          Could not load adversarial scenarios.
        </p>
      ) : !actionable || actionable.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No Maeve modes in your remit.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {actionable.map((entry) => (
            <AdversarialScenarioModeCard
              key={entry.modeKey}
              entry={entry}
              memberKind={memberKind}
              memberId={memberId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AdversarialScenarioModeCard({
  entry,
  memberKind,
  memberId,
}: {
  entry: AdversarialScenarioInspectorEntry;
  memberKind: CgcMemberKind | null;
  memberId: string | null;
}) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const alreadyApprovedByThisKind =
    entry.status.kind !== 'pending' &&
    memberKind !== null &&
    entry.status.reviewers.some((r) => r.cgcMemberKind === memberKind);

  const submitReview = useMutation({
    mutationFn: (body: { notes?: string }) =>
      api.post<{ ok: true }>(`/api/cgc/adversarial-scenarios/modes/${entry.modeKey}/review`, body),
    onSuccess: () => {
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'adversarial-scenarios'] });
    },
  });

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.modeKey}</span> · {entry.scenarios.length}{' '}
            scenario(s) · sign-off by{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint)' : ' (single)'}
          </p>
        </div>
        <AdversarialScenarioStatusBadge status={entry.status} />
      </div>

      <div className="mt-3 space-y-2">
        {entry.scenarios.length === 0 ? (
          <p className="text-xs text-muted-foreground">No scenarios authored for this mode yet.</p>
        ) : (
          <ul className="space-y-2">
            {entry.scenarios.map((s) => (
              <AdversarialScenarioRow key={s.id} scenario={s} />
            ))}
          </ul>
        )}
        {adding ? (
          <AdversarialScenarioAddForm modeKey={entry.modeKey} onCancel={() => setAdding(false)} />
        ) : (
          <Button variant="ghost" onClick={() => setAdding(true)} className="py-1 text-xs">
            + Add scenario
          </Button>
        )}
      </div>

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
          <p className="tracking-wide uppercase">Reviewers (current version)</p>
          <ul className="mt-1 space-y-0.5">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) ·{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {alreadyApprovedByThisKind ? (
        <p className="mt-3 text-xs text-muted-foreground">
          A reviewer of your kind has already signed off this mode's set for the current version.
        </p>
      ) : (
        <form
          className="mt-3 grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = notes.trim();
            submitReview.mutate(trimmed ? { notes: trimmed } : {});
          }}
        >
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Sign-off notes (optional)
            </span>
            <textarea
              className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Optional commentary recorded with the sign-off."
            />
          </label>
          <div>
            <Button
              type="submit"
              variant="primary"
              className="py-1.5 text-sm"
              loading={submitReview.isPending}
            >
              Sign off this mode's set for the current version
            </Button>
          </div>
          {submitReview.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {submitReview.error instanceof ApiError &&
              submitReview.error.message === 'kind_not_allowed'
                ? "Your CGC kind isn't in this mode's required-kind set."
                : submitReview.error instanceof ApiError &&
                    submitReview.error.message === 'already_reviewed'
                  ? 'Already signed off by your kind for the current version.'
                  : 'Could not submit. Try again.'}
            </p>
          ) : null}
        </form>
      )}

      <p className="mt-2 text-[10px] text-muted-foreground">
        {memberId ? `Authored actions attributed to ${memberId.slice(0, 8)}…` : null}
      </p>
    </li>
  );
}

function AdversarialScenarioStatusBadge({
  status,
}: {
  status: AdversarialScenarioInspectorEntry['status'];
}) {
  let label = 'Pending sign-off';
  let tone: 'pending' | 'partial' | 'ready' = 'pending';
  if (status.kind === 'partially_reviewed') {
    label = 'Partially signed off';
    tone = 'partial';
  } else if (status.kind === 'reviewed') {
    label = 'Signed off';
    tone = 'ready';
  }
  return (
    <span
      className={[
        'mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal uppercase',
        tone === 'pending' ? 'bg-muted text-muted-foreground' : '',
        tone === 'partial' ? 'bg-traffic-amber/10 text-traffic-amber-text' : '',
        tone === 'ready' ? 'bg-traffic-green/10 text-traffic-green-text' : '',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function AdversarialScenarioRow({ scenario }: { scenario: AdversarialScenarioRef }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  const retire = useMutation({
    mutationFn: () =>
      api.del<{ ok: true }>(`/api/cgc/adversarial-scenarios/scenarios/${scenario.id}`),
    onSuccess: () => {
      setConfirmingRetire(false);
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'adversarial-scenarios'] });
    },
  });

  return (
    <li className="rounded border border-border bg-background p-3 text-sm">
      <p className="font-medium">
        <span className="font-mono text-xs text-muted-foreground">{scenario.scenarioRef}</span> ·{' '}
        {scenario.title}
      </p>
      <p className="mt-1 text-xs tracking-wide text-muted-foreground uppercase">Scenario</p>
      <p className="whitespace-pre-wrap">{scenario.scenarioText}</p>
      <p className="mt-2 text-xs tracking-wide text-muted-foreground uppercase">
        Expected behaviour
      </p>
      <p className="whitespace-pre-wrap">{scenario.expectedBehaviour}</p>
      {scenario.rationale ? (
        <>
          <p className="mt-2 text-xs tracking-wide text-muted-foreground uppercase">Rationale</p>
          <p className="whitespace-pre-wrap">{scenario.rationale}</p>
        </>
      ) : null}
      <p className="mt-2 text-[10px] text-muted-foreground">
        Authored by {scenario.authoredBy.cgcMemberName} (
        {scenario.authoredBy.cgcMemberKind.replaceAll('_', ' ')}) on{' '}
        {new Date(scenario.createdAt).toLocaleString('en-GB')}
        {scenario.editedFromScenarioId ? ' (edited from prior version)' : ''}
        {scenario.notes ? ` · ${scenario.notes}` : ''}
      </p>
      <div className="mt-2 flex gap-2">
        <Button variant="ghost" onClick={() => setEditing(!editing)} className="py-0.5 text-[11px]">
          {editing ? 'Cancel edit' : 'Edit'}
        </Button>
        {confirmingRetire ? (
          <>
            <Button
              variant="secondary"
              onClick={() => retire.mutate()}
              loading={retire.isPending}
              className="py-0.5 text-[11px]"
            >
              Confirm retire
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmingRetire(false)}
              className="py-0.5 text-[11px]"
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            onClick={() => setConfirmingRetire(true)}
            className="py-0.5 text-[11px] text-traffic-red"
          >
            Retire
          </Button>
        )}
      </div>
      {editing ? (
        <AdversarialScenarioEditForm scenario={scenario} onCancel={() => setEditing(false)} />
      ) : null}
      {retire.error ? (
        <p className="mt-1 text-xs text-traffic-red" role="alert">
          {retire.error instanceof ApiError && retire.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this mode's required-kind set."
            : 'Could not retire the scenario. Try again.'}
        </p>
      ) : null}
    </li>
  );
}

function AdversarialScenarioAddForm({
  modeKey,
  onCancel,
}: {
  modeKey: string;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [scenarioRef, setScenarioRef] = useState('');
  const [title, setTitle] = useState('');
  const [scenarioText, setScenarioText] = useState('');
  const [expectedBehaviour, setExpectedBehaviour] = useState('');
  const [rationale, setRationale] = useState('');

  const submit = useMutation({
    mutationFn: (body: {
      scenarioRef: string;
      title: string;
      scenarioText: string;
      expectedBehaviour: string;
      rationale?: string;
    }) =>
      api.post<{ id: string }>(`/api/cgc/adversarial-scenarios/modes/${modeKey}/scenarios`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'adversarial-scenarios'] });
      onCancel();
    },
  });

  const canSubmit =
    scenarioRef.trim().length > 0 &&
    title.trim().length > 0 &&
    scenarioText.trim().length > 0 &&
    expectedBehaviour.trim().length > 0;

  return (
    <form
      className="mt-2 grid gap-2 rounded-md border border-border bg-muted/20 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const r = rationale.trim();
        submit.mutate({
          scenarioRef: scenarioRef.trim(),
          title: title.trim(),
          scenarioText: scenarioText.trim(),
          expectedBehaviour: expectedBehaviour.trim(),
          ...(r ? { rationale: r } : {}),
        });
      }}
    >
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Ref (e.g. EC-001)</span>
        <input
          className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
          value={scenarioRef}
          onChange={(e) => setScenarioRef(e.target.value)}
          maxLength={20}
          placeholder="EC-001"
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Title</span>
        <input
          className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="One-line summary of the adversarial probe"
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Scenario text</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={scenarioText}
          onChange={(e) => setScenarioText(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="The adversarial input / exchange to test Maeve against."
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Expected behaviour</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={expectedBehaviour}
          onChange={(e) => setExpectedBehaviour(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="What Maeve must do when this scenario fires."
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Rationale (optional)</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="Why this scenario matters clinically / legally."
        />
      </label>
      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          className="py-1 text-xs"
          loading={submit.isPending}
          disabled={!canSubmit || submit.isPending}
        >
          Add scenario
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="py-1 text-xs">
          Cancel
        </Button>
      </div>
      {submit.error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this mode's required-kind set."
            : submit.error instanceof ApiError && submit.error.message === 'duplicate_ref'
              ? 'A scenario with that ref already exists for this mode + version.'
              : 'Could not add the scenario. Try again.'}
        </p>
      ) : null}
    </form>
  );
}

function AdversarialScenarioEditForm({
  scenario,
  onCancel,
}: {
  scenario: AdversarialScenarioRef;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(scenario.title);
  const [scenarioText, setScenarioText] = useState(scenario.scenarioText);
  const [expectedBehaviour, setExpectedBehaviour] = useState(scenario.expectedBehaviour);
  const [rationale, setRationale] = useState(scenario.rationale ?? '');

  const submit = useMutation({
    mutationFn: (body: {
      title: string;
      scenarioText: string;
      expectedBehaviour: string;
      rationale?: string;
    }) =>
      api.patch<{ id: string }>(`/api/cgc/adversarial-scenarios/scenarios/${scenario.id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cgc', 'adversarial-scenarios'] });
      onCancel();
    },
  });

  const canSubmit =
    title.trim().length > 0 &&
    scenarioText.trim().length > 0 &&
    expectedBehaviour.trim().length > 0;

  return (
    <form
      className="mt-2 grid gap-2 rounded-md border border-border bg-muted/20 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const r = rationale.trim();
        submit.mutate({
          title: title.trim(),
          scenarioText: scenarioText.trim(),
          expectedBehaviour: expectedBehaviour.trim(),
          ...(r ? { rationale: r } : {}),
        });
      }}
    >
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Title</span>
        <input
          className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Scenario text</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={scenarioText}
          onChange={(e) => setScenarioText(e.target.value)}
          rows={3}
          maxLength={5000}
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Expected behaviour</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={expectedBehaviour}
          onChange={(e) => setExpectedBehaviour(e.target.value)}
          rows={2}
          maxLength={2000}
        />
      </label>
      <label className="block text-xs">
        <span className="tracking-wide text-muted-foreground uppercase">Rationale (optional)</span>
        <textarea
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={2}
          maxLength={2000}
        />
      </label>
      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          className="py-1 text-xs"
          loading={submit.isPending}
          disabled={!canSubmit || submit.isPending}
        >
          Save edit
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="py-1 text-xs">
          Cancel
        </Button>
      </div>
      {submit.error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {submit.error instanceof ApiError && submit.error.message === 'kind_not_allowed'
            ? "Your CGC kind isn't in this mode's required-kind set."
            : 'Could not save the edit. Try again.'}
        </p>
      ) : null}
    </form>
  );
}
